/**
 * Document template categories — used by the setup-templates script and
 * the Template Builder UI for long-form document templates (consent forms,
 * intake packets, letters, etc.).
 *
 * These are distinct from the Template Library tab categories in
 * `src/pages/settings/template-library/types.ts`. A document template's
 * `category` is stored on `meta.tag` with `DOCUMENT_TEMPLATE_CATEGORY_SYSTEM`.
 * The Template Library list/search uses `https://unify.health/fhir/CodeSystem/template-category`;
 * default mapping from document `category` → that tab is defined below (overridable via
 * frontmatter `templateLibraryTab` on file-driven templates).
 */

export const DOCUMENT_TEMPLATE_CATEGORY_SYSTEM =
  'https://unify.health/fhir/template-category';

export interface DocumentTemplateCategory {
  value: string;
  label: string;
  description?: string;
  sortOrder: number;
}

export const DOCUMENT_TEMPLATE_CATEGORIES: DocumentTemplateCategory[] = [
  { value: 'consent', label: 'Consent', description: 'Client consent and authorization documents', sortOrder: 10 },
  { value: 'intake', label: 'Intake', description: 'Onboarding and intake paperwork', sortOrder: 20 },
  { value: 'assessments', label: 'Assessments', description: 'Clinical assessments and evaluations', sortOrder: 30 },
  { value: 'treatment-plans', label: 'Treatment Plans', description: 'Treatment plans and care planning documents', sortOrder: 40 },
  { value: 'letters', label: 'Letters', description: 'Correspondence and formal letters', sortOrder: 50 },
  { value: 'billing', label: 'Billing', description: 'Billing and payment documents', sortOrder: 60 },
  { value: 'insurance', label: 'Insurance', description: 'Insurance verification and authorization documents', sortOrder: 70 },
  { value: 'administrative', label: 'Administrative', description: 'Administrative and operational documents', sortOrder: 80 },
  { value: 'clinical', label: 'Clinical', description: 'Clinical documentation templates', sortOrder: 90 },
  { value: 'discharge', label: 'Discharge', description: 'Discharge summaries and transition documents', sortOrder: 100 },
  { value: 'other', label: 'Other', description: 'Uncategorized document templates', sortOrder: 999 },
];

const VALID_CATEGORY_VALUES = new Set(
  DOCUMENT_TEMPLATE_CATEGORIES.map((c) => c.value),
);

export function isValidDocumentCategory(value: string): boolean {
  return VALID_CATEGORY_VALUES.has(value);
}

export function getDocumentCategoryLabel(value: string): string | undefined {
  return DOCUMENT_TEMPLATE_CATEGORIES.find((c) => c.value === value)?.label;
}

// ---------------------------------------------------------------------------
// Template Library tab tags (`meta.tag` system: unify.health/fhir/CodeSystem/template-category)
// ---------------------------------------------------------------------------

/** Same URI as `TEMPLATE_CATEGORY_SYSTEM` in `template-library/types.ts`. */
export const TEMPLATE_LIBRARY_CATEGORY_SYSTEM =
  'https://unify.health/fhir/CodeSystem/template-category';

/** Tab codes — must match `TEMPLATE_CATEGORIES` in `template-library/types.ts`. */
export type TemplateLibraryTabCode =
  | 'appointments'
  | 'measures'
  | 'treatment-planning'
  | 'forms'
  | 'administrative';

export const TEMPLATE_LIBRARY_TAB_CODES: readonly TemplateLibraryTabCode[] = [
  'appointments',
  'measures',
  'treatment-planning',
  'forms',
  'administrative',
] as const;

export function isTemplateLibraryTabCode(value: string): value is TemplateLibraryTabCode {
  return (TEMPLATE_LIBRARY_TAB_CODES as readonly string[]).includes(value);
}

/** Document category value → Template Library tab (every DOCUMENT_TEMPLATE_CATEGORIES value listed). */
const DOCUMENT_CATEGORY_TO_LIBRARY_TAB: Record<string, TemplateLibraryTabCode> = {
  consent: 'forms',
  intake: 'forms',
  billing: 'forms',
  insurance: 'forms',
  other: 'forms',

  assessments: 'measures',

  'treatment-plans': 'treatment-planning',
  clinical: 'treatment-planning',
  discharge: 'treatment-planning',

  letters: 'administrative',
  administrative: 'administrative',
};

export function libraryTabForDocumentCategory(
  documentCategory: string,
): TemplateLibraryTabCode {
  return DOCUMENT_CATEGORY_TO_LIBRARY_TAB[documentCategory] ?? 'forms';
}
